// Billing rules (brief 1E-35; feedback 2026-11-02 part 3, "Most important" 1 and 2; mockup v93).
// Pure: no database, no clock.
//
// - Bill by is set on the client's contract, and an authorization can set its own: Hours worked
//   (check-in to check-out), Scheduled hours (the schedule's hours when the visit is worked), The
//   shift (each covered shift at full length), The day (the shifts covered that day add up to one
//   line). It replaces the 24/7 special case: 2, 6, 12, 16 or 24 hours a day is the schedule.
// - A visit billed by the shift or the day never bills clock minutes: when its times don't cover
//   its shift it's held ("Shift not covered: check the times"), never billed short.
// - Claim lines: by visit or shift, by day (one line per day however many caregivers, the shifts
//   listed under it) or by week; units in the contract's unit.
// - Invoicing weekly, every two weeks or monthly, with the period printed "Week of" or "Week ending".
// - The billing packet: the claim or invoice, then each visit note for the dates billed (a
//   missing one is named), timesheets, the current care plan.
// Every choice is the company's data; nothing here names a payer.

import type { HoursBilledSetting } from "./billed-hours";
import { addDays, usShort } from "./week";

type Read<T> = { ok: true; value: T } | { ok: false; error: string };
const no = <T>(error: string): Read<T> => ({ ok: false, error });

export const BILL_BY = ["worked", "scheduled", "shift", "day"] as const;
export type BillBy = (typeof BILL_BY)[number];
export const BILL_BY_LABEL: Record<BillBy, string> = {
  worked: "Hours worked",
  scheduled: "Scheduled hours",
  shift: "The shift",
  day: "The day",
};
export const BILL_BY_HINT: Record<BillBy, string> = {
  worked: "From check-in to check-out, less breaks.",
  scheduled: "The hours on the schedule, when the visit is worked.",
  shift: "Each shift covered, at its full length.",
  day: "Each day covered: the shifts that day add up to one line.",
};

export const BILL_UNITS = ["hour", "quarter_hour", "visit", "day"] as const;
export type BillUnit = (typeof BILL_UNITS)[number];
export const BILL_UNIT_LABEL: Record<BillUnit, string> = {
  hour: "Hour",
  quarter_hour: "15 minutes",
  visit: "Visit",
  day: "Day",
};

export const CLAIM_LINES = ["visit", "day", "week"] as const;
export type ClaimLines = (typeof CLAIM_LINES)[number];
export const CLAIM_LINES_LABEL: Record<ClaimLines, string> = {
  visit: "By visit or shift",
  day: "By day",
  week: "By week",
};
export const CLAIM_LINES_HINT: Record<ClaimLines, string> = {
  visit: "One line per visit or shift.",
  day: "One line per day, however many caregivers.",
  week: "One line per service for the week.",
};

export const INVOICING = ["weekly", "two_weeks", "monthly"] as const;
export type Invoicing = (typeof INVOICING)[number];
export const INVOICING_LABEL: Record<Invoicing, string> = {
  weekly: "Weekly",
  two_weeks: "Every two weeks",
  monthly: "Monthly",
};

export const PERIOD_LABELS = ["week_of", "week_ending"] as const;
export type PeriodLabel = (typeof PERIOD_LABELS)[number];
export const PERIOD_LABEL_WORDS: Record<PeriodLabel, string> = {
  week_of: "Week of",
  week_ending: "Week ending",
};

/** A contract's billing rules. */
export interface BillingRules {
  billBy: BillBy;
  unit: BillUnit;
  claimLines: ClaimLines;
  invoicing: Invoicing;
  periodLabel: PeriodLabel;
}

/** What the billing packet carries besides the claim or invoice (a client setting). */
export interface PacketSetting {
  notes: boolean;
  timesheets: boolean;
  carePlan: boolean;
}

const pick = <T extends string>(list: readonly T[], v: unknown, d: T): T =>
  list.includes(v as T) ? (v as T) : d;

/** A contract's rules as its row keeps them; a contract that's missing reads as Hours worked. */
export function billingRulesOf(
  c: {
    bill_by?: string | null;
    bill_unit?: string | null;
    claim_lines?: string | null;
    invoicing?: string | null;
    period_label?: string | null;
  } | null,
): BillingRules {
  return {
    billBy: pick(BILL_BY, c?.bill_by, "worked"),
    unit: pick(BILL_UNITS, c?.bill_unit, "hour"),
    claimLines: pick(CLAIM_LINES, c?.claim_lines, "visit"),
    invoicing: pick(INVOICING, c?.invoicing, "weekly"),
    periodLabel: pick(PERIOD_LABELS, c?.period_label, "week_of"),
  };
}

/** A client's billing packet as its row keeps it. */
export const packetOf = (o: {
  packet_notes?: boolean | null;
  packet_timesheets?: boolean | null;
  packet_care_plan?: boolean | null;
}): PacketSetting => ({
  notes: o.packet_notes === true,
  timesheets: o.packet_timesheets === true,
  carePlan: o.packet_care_plan === true,
});

/**
 * Reads a change to a client's billing rules: the contract's (Bill by, Unit, Claim lines,
 * Invoicing, Period on the invoice) and the client's (Vendor number, the packet). What isn't sent
 * keeps what it had.
 */
export function readBillingRules(
  input: unknown,
  had: { rules: BillingRules; vendor: string | null; packet: PacketSetting },
): Read<{ rules: BillingRules; vendor: string | null; packet: PacketSetting }> {
  const b = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  const one = <T extends string>(key: string, list: readonly T[], d: T): T | null =>
    b[key] === undefined ? d : list.includes(b[key] as T) ? (b[key] as T) : null;
  const billBy = one("billBy", BILL_BY, had.rules.billBy);
  if (billBy === null) return no("Pick how this client is billed.");
  const unit = one("unit", BILL_UNITS, had.rules.unit);
  if (unit === null) return no("Pick the unit.");
  const claimLines = one("claimLines", CLAIM_LINES, had.rules.claimLines);
  if (claimLines === null) return no("Pick how claim lines group.");
  const invoicing = one("invoicing", INVOICING, had.rules.invoicing);
  if (invoicing === null) return no("Pick how often to invoice.");
  const periodLabel = one("periodLabel", PERIOD_LABELS, had.rules.periodLabel);
  if (periodLabel === null) return no("Pick how the period prints.");
  let vendor = had.vendor;
  if (b.vendorNumber !== undefined) {
    if (b.vendorNumber !== null && typeof b.vendorNumber !== "string")
      return no("Type the vendor number.");
    const v = typeof b.vendorNumber === "string" ? b.vendorNumber.trim() : "";
    if (v.length > 40) return no("A vendor number is up to 40 characters.");
    vendor = v || null;
  }
  const p = (typeof b.packet === "object" && b.packet !== null ? b.packet : {}) as Record<
    string,
    unknown
  >;
  const flag = (k: keyof PacketSetting) =>
    typeof p[k] === "boolean" ? (p[k] as boolean) : had.packet[k];
  return {
    ok: true,
    value: {
      rules: { billBy, unit, claimLines, invoicing, periodLabel },
      vendor,
      packet: { notes: flag("notes"), timesheets: flag("timesheets"), carePlan: flag("carePlan") },
    },
  };
}

/** The card's summary line: "The day · By day · Weekly · Vendor no. 14420". */
export const rulesWords = (r: BillingRules, vendor: string | null) =>
  [
    BILL_BY_LABEL[r.billBy],
    CLAIM_LINES_LABEL[r.claimLines],
    INVOICING_LABEL[r.invoicing],
    vendor ? `Vendor no. ${vendor}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** The Bill by that applies: an authorization's own wins over its contract's. */
export const billByFor = (contract: BillBy, auth: { bill_by: BillBy | null } | null): BillBy =>
  auth?.bill_by ?? contract;

/** Reads an authorization's own Bill by: one of the four, or "" / null for the contract's. */
export function readAuthBillBy(v: unknown): Read<BillBy | null> {
  if (v === null || v === "" || v === undefined) return { ok: true, value: null };
  return BILL_BY.includes(v as BillBy) ? { ok: true, value: v as BillBy } : no("Pick Bill by.");
}

// ---- The hours a visit bills ----------------------------------------------------------------------

/** Why a visit billed by the shift or the day is held. */
export const SHIFT_NOT_COVERED = "Shift not covered: check the times";

/** A visit's scheduled shift and its times (check-in and check-out, when they're there). */
export interface VisitTimes {
  startsAt: Date;
  endsAt: Date;
  inAt: Date | null;
  outAt: Date | null;
  /** Breaks taken, in minutes (none until 1E-36 records them). */
  breakMinutes?: number;
}

const minutesBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 60_000);

/**
 * Whether a visit's times cover its shift: both times there, and the time inside the shift short
 * of the shift by no more than the minutes allowed (the client's short-visit minutes).
 */
export function shiftCovered(t: VisitTimes, allowedShortMinutes: number): boolean {
  if (!t.inAt || !t.outAt || t.outAt <= t.inAt) return false;
  const from = Math.max(t.inAt.getTime(), t.startsAt.getTime());
  const to = Math.min(t.outAt.getTime(), t.endsAt.getTime());
  const inside = Math.max(0, Math.round((to - from) / 60_000));
  return minutesBetween(t.startsAt, t.endsAt) - inside <= allowedShortMinutes;
}

export type BilledFrom = "worked" | "scheduled" | "set" | "change" | "shift" | "day";

export type VisitBill =
  { ok: true; minutes: number; hours: number; from: BilledFrom } | { ok: false; held: string };

const hoursOf = (m: number) => Math.round((m / 60) * 100) / 100;
const of = (minutes: number, from: BilledFrom): VisitBill => ({
  ok: true,
  minutes: Math.max(0, Math.round(minutes)),
  hours: hoursOf(Math.max(0, minutes)),
  from,
});

/**
 * The hours a visit bills under its Bill by:
 * - Hours worked: check-in to check-out, less breaks (the client's set hours a visit when it
 *   bills a set number, 1E-13);
 * - Scheduled hours: the schedule's hours, unless worked is short of them by more than the
 *   client's minutes (then what was worked);
 * - The shift and The day: the shift at its full length when the times cover it; otherwise held,
 *   never clock minutes.
 * A change of billed hours with its reason wins under hours worked and scheduled hours.
 */
export function visitBill(
  billBy: BillBy,
  t: VisitTimes,
  setting: HoursBilledSetting,
  change: { hours: number | string } | null,
): VisitBill {
  const scheduled = minutesBetween(t.startsAt, t.endsAt);
  if (billBy === "shift" || billBy === "day") {
    if (!shiftCovered(t, setting.shortMinutes)) return { ok: false, held: SHIFT_NOT_COVERED };
    return of(scheduled, billBy);
  }
  const worked = Math.max(
    0,
    minutesBetween(t.inAt ?? t.startsAt, t.outAt ?? t.endsAt) - (t.breakMinutes ?? 0),
  );
  if (change) return of(Number(change.hours) * 60, "change");
  if (billBy === "scheduled")
    return scheduled - worked > setting.shortMinutes
      ? of(worked, "worked")
      : of(scheduled, "scheduled");
  if (setting.how === "set" && setting.setHours !== null) return of(setting.setHours * 60, "set");
  return of(worked, "worked");
}

const clock = (at: Date, zone: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" }).format(
    at,
  );
const md = (at: Date, zone: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "numeric", day: "numeric" }).format(at);

/**
 * The times a held visit has, in words: "Check-in 4:37 PM, check-out 4:41 PM (4 minutes) on a
 * 7:00 AM – 3:00 PM shift". A time on another day than the shift's start carries its date.
 */
export function heldTimesWords(t: VisitTimes, zone: string): string {
  const day = md(t.startsAt, zone);
  const at = (d: Date) =>
    md(d, zone) === day ? clock(d, zone) : `${clock(d, zone)} ${md(d, zone)}`;
  const shift = `${clock(t.startsAt, zone)} – ${clock(t.endsAt, zone)} shift`;
  if (!t.inAt && !t.outAt) return `No check-in or check-out on a ${shift}`;
  if (!t.inAt) return `No check-in; check-out ${at(t.outAt as Date)} on a ${shift}`;
  if (!t.outAt) return `Check-in ${at(t.inAt)}, no check-out on a ${shift}`;
  const m = Math.max(0, minutesBetween(t.inAt, t.outAt));
  const len = m < 60 ? `${m} minute${m === 1 ? "" : "s"}` : `${hoursOf(m).toFixed(2)} hours`;
  return `Check-in ${at(t.inAt)}, check-out ${at(t.outAt)} (${len}) on a ${shift}`;
}

// ---- Claim lines -----------------------------------------------------------------------------------

/** One billed line as the claim lines read it. */
export interface BilledLine {
  id: string;
  day: string;
  patientId: string;
  serviceId: string;
  /** The line's units in its own unit, and that unit. */
  units: number;
  unit: "hour" | "quarter_hour" | "visit" | "day" | "mile";
  amountCents: number;
  /** The shift it bills: "7 AM–3 PM Aisha R." (none for a line not from a visit). */
  shift?: string | null;
}

/** One claim line: a visit or shift, a day, or a week of one service. */
export interface ClaimLine {
  key: string;
  from: string;
  to: string;
  patientId: string;
  serviceId: string;
  units: number;
  unit: BillUnit;
  amountCents: number;
  /** The rate a unit (the amount over the units), in cents; null with no units. */
  rateCents: number | null;
  lineIds: string[];
  /** The shifts under it, in order. */
  shifts: string[];
}

/**
 * Units in the contract's unit: hours and 15-minute units convert into each other; a visit
 * counts each line; a day counts each day. A line already in another unit keeps its count.
 */
export function unitsIn(
  unit: BillUnit,
  lines: readonly Pick<BilledLine, "units" | "unit" | "day">[],
) {
  if (unit === "day") return new Set(lines.map((l) => l.day)).size;
  if (unit === "visit") return lines.length;
  let total = 0;
  for (const l of lines) {
    const n = Number(l.units);
    if (unit === "hour") total += l.unit === "quarter_hour" ? n / 4 : n;
    else total += l.unit === "hour" ? n * 4 : n;
  }
  return Math.round(total * 100) / 100;
}

/**
 * The claim lines, as the contract sets them: one per visit or shift; one per patient, service and
 * day (however many caregivers, the shifts listed under it); or one per patient and service for
 * the week (from the week's first day). Units in the contract's unit. In date order.
 */
export function claimLines(
  lines: readonly BilledLine[],
  how: ClaimLines,
  unit: BillUnit,
  weekStartOf: (day: string) => string,
): ClaimLine[] {
  const groups = new Map<string, BilledLine[]>();
  for (const l of lines) {
    const key =
      how === "visit"
        ? `${l.day}|${l.id}`
        : how === "day"
          ? `${l.day}|${l.patientId}|${l.serviceId}`
          : `${weekStartOf(l.day)}|${l.patientId}|${l.serviceId}`;
    groups.set(key, [...(groups.get(key) ?? []), l]);
  }
  return [...groups.entries()]
    .map(([key, list]) => {
      const sorted = [...list].sort((a, b) => a.day.localeCompare(b.day));
      const first = sorted[0] as BilledLine;
      // A visit line keeps its own units when the contract's unit can't count it (a visit priced
      // per visit on an hourly contract stays one visit).
      const units = how === "visit" && unit === "day" ? 1 : unitsIn(unit, sorted);
      const amountCents = sorted.reduce((t, l) => t + l.amountCents, 0);
      return {
        key,
        from: first.day,
        to: (sorted.at(-1) as BilledLine).day,
        patientId: first.patientId,
        serviceId: first.serviceId,
        units,
        unit,
        amountCents,
        rateCents: units > 0 ? Math.round(amountCents / units) : null,
        lineIds: sorted.map((l) => l.id),
        shifts: sorted.map((l) => l.shift ?? "").filter(Boolean),
      };
    })
    .sort(
      (a, b) =>
        a.from.localeCompare(b.from) ||
        a.patientId.localeCompare(b.patientId) ||
        a.serviceId.localeCompare(b.serviceId),
    );
}

/** "24 units" or "8.00 h" for a claim line, in the contract's unit. */
export function claimUnitsWords(n: number, unit: BillUnit): string {
  if (unit === "hour") return `${n.toFixed(2)} h`;
  if (unit === "quarter_hour") return `${n} unit${n === 1 ? "" : "s"}`;
  if (unit === "day") return `${n} day${n === 1 ? "" : "s"}`;
  return `${n} visit${n === 1 ? "" : "s"}`;
}

// ---- The invoice period ---------------------------------------------------------------------------

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Whole weeks between two week starts (b after a: positive). */
const weeksBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / (7 * 86_400_000));

/**
 * The invoice period a day falls in: its billing week (weekly); two billing weeks counted from the
 * anchor's week (every two weeks: the contract's first billing week, so periods never move); or
 * the calendar month (monthly). Days are already on the company's clock.
 */
export function invoicePeriod(
  day: string,
  invoicing: Invoicing,
  weekStart: string,
  anchorWeekStart: string | null,
): { start: string; end: string } {
  if (invoicing === "monthly") {
    const start = `${day.slice(0, 7)}-01`;
    const next = new Date(Date.UTC(+day.slice(0, 4), +day.slice(5, 7), 1));
    return { start, end: addDays(next.toISOString().slice(0, 10), -1) };
  }
  if (invoicing === "two_weeks") {
    const odd = anchorWeekStart
      ? Math.abs(weeksBetween(anchorWeekStart, weekStart)) % 2 === 1
      : false;
    const start = odd ? addDays(weekStart, -7) : weekStart;
    return { start, end: addDays(start, 13) };
  }
  return { start: weekStart, end: addDays(weekStart, 6) };
}

/** Whether an invoice period has ended as of a day (its last day is before it). */
export const periodEnded = (p: { end: string }, today: string) => p.end < today;

/**
 * The period as the invoice prints it: "Week of Nov 1" or "Week ending Nov 7"; two weeks add
 * the span ("Week ending Nov 14 (Nov 1–14)"); a month is "Month of November 2026".
 */
export function periodWords(
  p: { start: string; end: string },
  invoicing: Invoicing,
  label: PeriodLabel,
): string {
  if (invoicing === "monthly")
    return `Month of ${MONTH_NAMES[+p.start.slice(5, 7) - 1]} ${p.start.slice(0, 4)}`;
  const head = `${PERIOD_LABEL_WORDS[label]} ${usShort(label === "week_of" ? p.start : p.end)}`;
  if (invoicing === "weekly") return head;
  const a = usShort(p.start);
  const b = usShort(p.end);
  const span = a.split(" ")[0] === b.split(" ")[0] ? `${a}–${b.split(" ")[1]}` : `${a} – ${b}`;
  return `${head} (${span})`;
}

// ---- The billing packet ---------------------------------------------------------------------------

/** One visit billed on the invoice, as the packet reads it. */
export interface PacketVisit {
  visitId: string;
  day: string;
  /** "Sun 11/01 7 AM–3 PM · Aisha R." */
  words: string;
  noteIn: boolean;
  timesheet: boolean;
  /** Resubmitted with records: its note goes in whatever the client's packet says. */
  withRecords?: boolean;
}

export interface PacketPage {
  kind: "claim" | "note" | "timesheet" | "care_plan";
  page: string;
  what: string;
  /** ready; in (a note in); missing; waiting (the claim isn't approved yet). */
  status: "ready" | "in" | "missing" | "waiting";
  visitId?: string;
}

/**
 * The packet's pages, in order: the claim (CMS-1500) or the invoice, then each visit note for
 * the dates billed, the timesheets, the current care plan, as the client sets it. A note,
 * timesheet or care plan that isn't there is named as missing.
 */
export function packetPages(input: {
  claim: "CMS-1500" | "Invoice";
  claimPages: string;
  /** Whether the claim's pages are ready to go (an approved CMS-1500, or the invoice). */
  claimReady?: boolean;
  setting: PacketSetting;
  visits: readonly PacketVisit[];
  carePlan: boolean;
}): { pages: PacketPage[]; missing: PacketPage[]; notes: { in: number; of: number } } {
  const pages: PacketPage[] = [
    {
      kind: "claim",
      page: input.claim,
      what: input.claimPages,
      status: input.claimReady === false ? "waiting" : "ready",
    },
  ];
  const sorted = [...input.visits].sort((a, b) => a.day.localeCompare(b.day));
  const noted = sorted.filter((v) => input.setting.notes || v.withRecords);
  for (const v of noted)
    pages.push({
      kind: "note",
      page: "Visit note",
      what: v.words,
      status: v.noteIn ? "in" : "missing",
      visitId: v.visitId,
    });
  if (input.setting.timesheets)
    for (const v of sorted)
      pages.push({
        kind: "timesheet",
        page: "Timesheet",
        what: v.words,
        status: v.timesheet ? "in" : "missing",
        visitId: v.visitId,
      });
  if (input.setting.carePlan)
    pages.push({
      kind: "care_plan",
      page: "Care plan",
      what: "Current care plan",
      status: input.carePlan ? "in" : "missing",
    });
  return {
    pages,
    missing: pages.filter((p) => p.status === "missing"),
    notes: { in: noted.filter((v) => v.noteIn).length, of: noted.length },
  };
}

/** The packet card's summary: "CMS-1500 · 7 of 8 visit notes". */
export const packetWords = (claim: string, notes: { in: number; of: number }) =>
  notes.of ? `${claim} · ${notes.in} of ${notes.of} visit notes` : claim;

/** "Visit note for Tue 11/03 11 PM–7 AM · Monique P." for each page missing. */
export const missingWords = (missing: readonly PacketPage[]) =>
  missing.map((p) => `${p.page} for ${p.what}`).join(", ");

/**
 * Send packet: with a page missing it asks first; sending anyway needs the answer. Null when it
 * can go.
 */
export function refuseSendPacket(
  missing: readonly PacketPage[],
  confirmed: boolean,
): string | null {
  if (!missing.length || confirmed) return null;
  return `Missing: ${missingWords(missing).replace(/\.$/, "")}. Send anyway?`;
}

// ---- Payments ---------------------------------------------------------------------------------------

/** A remittance FlowCare couldn't read, in plain words. */
export const REMIT_UNREAD = "The pages couldn't be read. Try again, or type the lines in.";

/** Reads a payer's code on a short or denied line: up to 20 letters, numbers and dashes. */
export function readPayerCode(v: unknown): Read<string | null> {
  const s = typeof v === "string" ? v.trim().toUpperCase() : "";
  if (!s) return { ok: true, value: null };
  if (s.length > 20 || !/^[A-Z0-9-]+$/.test(s))
    return no("A code is up to 20 letters, numbers and dashes.");
  return { ok: true, value: s };
}

/** Resubmit with records: only a denied line still open, on an invoice that went out. */
export function refuseResubmit(line: {
  status: string;
  open: number;
  invoiceStatus: string;
  rebilled: boolean;
}): string | null {
  if (line.rebilled) return "This line was already rebilled.";
  if (line.status !== "denied" && line.status !== "short")
    return "Only a short or denied line is resubmitted.";
  if (line.open <= 0) return "Nothing is open on this line.";
  if (line.invoiceStatus === "void") return "The invoice is void.";
  return null;
}
